import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, WS_BASE_URL } from '../config';
import './Capture.css';

function Capture() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Ready to start');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const audioChunksRef = useRef([]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sessions/${sessionId}`);
      setSession(response.data);
    } catch (err) {
      console.error('Error fetching session:', err);
      setError('Session not found');
    }
  };

  useEffect(() => {
    fetchSession();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const connectWebSocket = () => {
    const ws = new WebSocket(`${WS_BASE_URL}/ws/capture/${sessionId}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setStatus('Connected');
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error');
      setStatus('Connection error');
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
      setStatus('Disconnected');
    };
    
    wsRef.current = ws;
  };

  const startRecording = async () => {
    try {
      setError('');
      setStatus('Requesting microphone access...');

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });
      
      console.log('Audio track settings:', stream.getAudioTracks()[0].getSettings());

      connectWebSocket();

      // Try different formats to find one that works
      let mimeType = '';
      const formats = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        '' // Browser default
      ];
      
      for (const format of formats) {
        if (format === '' || MediaRecorder.isTypeSupported(format)) {
          mimeType = format;
          console.log('Using MIME type:', mimeType || 'browser default');
          break;
        }
      }

      const options = { audioBitsPerSecond: 128000 };
      if (mimeType) {
        options.mimeType = mimeType;
      }
      
      const mediaRecorder = new MediaRecorder(stream, options);
      console.log('MediaRecorder created with:', mediaRecorder.mimeType);

      mediaRecorderRef.current = mediaRecorder;
      
      let chunkCount = 0;
      let isRecordingActive = true;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunkCount++;
          console.log(`🎵 Complete audio segment #${chunkCount}: ${event.data.size} bytes`);
          
          // Send complete WebM file via WebSocket
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Data = reader.result.split(',')[1];
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              console.log(`📤 Sending complete segment #${chunkCount}`);
              wsRef.current.send(JSON.stringify({
                type: 'audio_chunk',
                data: base64Data
              }));
              
              // Restart recording to get next complete file
              if (isRecordingActive && mediaRecorderRef.current) {
                mediaRecorderRef.current.start();
                setTimeout(() => {
                  if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    mediaRecorderRef.current.stop(); // Stop to trigger ondataavailable
                  }
                }, 3000); // 3 second segments
              }
            }
          };
          reader.readAsDataURL(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log('🛑 MediaRecorder stopped');
        // Only clean up if user manually stopped
        if (!isRecordingActive) {
          stream.getTracks().forEach(track => track.stop());
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'stop' }));
          }
        }
      };

      // Start initial recording
      console.log('🎙 Starting segmented recording (3-second complete files)...');
      mediaRecorder.start();
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop(); // This triggers ondataavailable with complete file
        }
      }, 3000);
      
      setIsRecording(true);
      setStatus('Recording...');
      
      // Store cleanup function
      window.stopRecordingSegments = () => {
        isRecordingActive = false;
      };

    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to access microphone. Please grant permission.');
      setStatus('Error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('🛑 User stopped recording');
      // Signal to stop the segment loop
      if (window.stopRecordingSegments) {
        window.stopRecordingSegments();
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStatus('Stopped');
    }
  };

  const copyViewerLink = () => {
    const viewerUrl = `${window.location.origin}/viewer`;
    navigator.clipboard.writeText(viewerUrl);
    alert('Viewer link copied to clipboard!');
  };

  const ACCEPTED_FILE_TYPES = '.mov,.m4a,.mp3,.mp4,.mpeg,.mpga,.wav,.webm';
  const MAX_FILE_SIZE_MB = 25;

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    setUploadError('');
    setUploadSuccess('');
    if (!file) {
      setUploadFile(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setUploadError(`File must be under ${MAX_FILE_SIZE_MB} MB`);
      setUploadFile(null);
      e.target.value = '';
      return;
    }
    setUploadFile(file);
  };

  const submitUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const response = await axios.post(
        `${API_BASE_URL}/sessions/${sessionId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        }
      );
      const segments = response.data?.segments ?? 1;
      setUploadSuccess(
        `Processed "${uploadFile.name}": ${segments} segment(s) transcribed and translated. ` +
        (segments > 1 ? 'Results were sent to the viewer as they were ready.' : '')
      );
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchSession();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      const text = Array.isArray(msg)
        ? msg.map((m) => (typeof m === 'object' && m?.msg) ? m.msg : String(m)).join(', ')
        : String(msg);
      setUploadError(text);
    } finally {
      setUploading(false);
    }
  };

  if (!session) {
    return (
      <div className="capture-container">
        <div className="capture-card">
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="capture-container">
      <div className="capture-card">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back
        </button>

        <h1 className="title">🎤 Audio Capture</h1>
        
        <div className="session-info">
          <p><strong>Session ID:</strong> {sessionId}</p>
          <p><strong>Translation:</strong> {session.source_lang.toUpperCase()} → {session.target_lang.toUpperCase()}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="status-display">
          <div className={`status-indicator ${isRecording ? 'recording' : ''}`}></div>
          <span className="status-text">{status}</span>
        </div>

        <div className="controls">
          {!isRecording ? (
            <button className="btn btn-start" onClick={startRecording}>
              🎙 Start Recording
            </button>
          ) : (
            <button className="btn btn-stop" onClick={stopRecording}>
              ⏹ Stop Recording
            </button>
          )}
        </div>

        <div className="upload-section">
          <h3 className="upload-title">📁 Or upload a file</h3>
          <p className="upload-hint">.mov, .m4a, .mp3, .mp4, .mpeg, .mpga, .wav, .webm — max {MAX_FILE_SIZE_MB} MB. Processed in chunks so results appear on the viewer as they’re ready.</p>
          <p className="upload-tip">💡 Open the viewer link in another tab to see results while the file processes.</p>
          <div className="upload-controls">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={onFileSelect}
              className="file-input"
              disabled={uploading}
            />
            <button
              type="button"
              className="btn btn-upload"
              onClick={submitUpload}
              disabled={!uploadFile || uploading}
            >
              {uploading ? '⏳ Processing...' : '📤 Upload & transcribe'}
            </button>
          </div>
          {uploadFile && !uploading && (
            <p className="upload-filename">Selected: {uploadFile.name}</p>
          )}
          {uploadError && <div className="upload-error">{uploadError}</div>}
          {uploadSuccess && <div className="upload-success">{uploadSuccess}</div>}
        </div>

        <div className="share-section">
          <p className="share-text">Share this link with viewers:</p>
          <div className="link-container">
            <input 
              type="text" 
              readOnly 
              value={`${window.location.origin}/viewer`}
              className="link-input"
            />
            <button className="btn-copy" onClick={copyViewerLink}>
              📋 Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Capture;
